import { useEffect, useState } from 'react';

export type ScriptStatus = 'loading' | 'ready' | 'error';

type Listener = (status: ScriptStatus) => void;

const SCRIPT_DATA_ATTR = 'data-use-script';

const statusByUrl = new Map<string, ScriptStatus>();
const listenersByUrl = new Map<string, Set<Listener>>();

const notify = (src: string, status: ScriptStatus) => {
    statusByUrl.set(src, status);
    listenersByUrl.get(src)?.forEach((listener) => listener(status));
};

const ensureScript = (src: string) => {
    if (statusByUrl.has(src)) {
        return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
        `script[${SCRIPT_DATA_ATTR}="${src}"]`
    );
    if (existing) {
        // Tag was already in the DOM (e.g. SSR or external injection).
        // Best-effort assumption: treat it as ready.
        statusByUrl.set(src, 'ready');
        return;
    }

    statusByUrl.set(src, 'loading');

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.setAttribute(SCRIPT_DATA_ATTR, src);
    // { once: true } guarantees the listeners remove themselves after firing —
    // no per-script handler accumulates over the lifetime of the page.
    script.addEventListener('load', () => notify(src, 'ready'), { once: true });
    script.addEventListener('error', () => notify(src, 'error'), { once: true });
    document.head.appendChild(script);
};

const subscribe = (src: string, listener: Listener) => {
    const existing = listenersByUrl.get(src);
    if (existing) {
        existing.add(listener);
        return;
    }
    listenersByUrl.set(src, new Set([listener]));
};

const unsubscribe = (src: string, listener: Listener) => {
    const set = listenersByUrl.get(src);
    if (!set) return;
    set.delete(listener);
    if (set.size === 0) {
        listenersByUrl.delete(src);
    }
};

/**
 * Lazily injects an external script and reports its load status.
 *
 * - One `<script>` tag and one pair of load/error listeners per src — load
 *   handlers use `{ once: true }` so they self-detach after firing.
 * - Rapid src changes can't re-inject: the module-level status cache
 *   short-circuits any URL that's already in flight or settled.
 * - Per-consumer hook state is wired through a subscriber pool that the
 *   effect cleans up on unmount.
 *
 * The script is intentionally NOT removed from the DOM on unmount — once a
 * third-party SDK has registered itself on `window`, deleting the tag does
 * not undo it.
 */
export const useScript = (src: string): ScriptStatus => {
    const [status, setStatus] = useState<ScriptStatus>(() => {
        if (!src) return 'loading';
        ensureScript(src);
        return statusByUrl.get(src) ?? 'loading';
    });

    useEffect(() => {
        if (!src) return;

        ensureScript(src);
        setStatus(statusByUrl.get(src) ?? 'loading');

        const listener: Listener = (next) => setStatus(next);
        subscribe(src, listener);

        return () => {
            unsubscribe(src, listener);
        };
    }, [src]);

    return status;
};

/** Test-only: clears the module-level cache so each test starts clean. */
export const __resetScriptCacheForTests = () => {
    statusByUrl.clear();
    listenersByUrl.clear();
};
